import {
    captureWalletEvent,
    eventError,
    eventId,
    migrationStep,
    ownerForAddress
} from "@shared/analytics/walletEvents";
import { LEGACY_USDV_DECIMALS, LEGACY_USDV_MINT, USDV_DECIMALS, USDV_MINT } from "@shared/tokens/usdv";
import type { TransactionObserver } from "@shared/types/transactionObserver";
import {
    getUsdvMigrationCurrentStep,
    TransactionError,
    type UsdvMigrationCardConfig,
    type UsdvMigrationPlan,
    useTokenBalancesActions,
    useTokenBalancesData,
    useWallet,
    withWalletBalanceMutationLock
} from "@solomon-apps/shared";
import { BigDecimal } from "@solomon-labs/bigdecimal";
import { WELL_KNOWN_TOKENS_STRINGS } from "@solomon-labs/constants";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

import { useCanUnstakeSusdv, useStakingVaultDerived } from "@/hooks/staking";
import { useStartUnstakeTransaction } from "@/hooks/staking/useStartUnstakeTransaction";
import { useUnstakeTransaction } from "@/hooks/staking/useUnstakeTransaction";
import { buildJupiterExecuteParams, type JupiterUltraQuoteResponse } from "@/hooks/swap/jupiterPayload";
import { useJupiterExecute, useJupiterQuote } from "@/hooks/swap/useJupiter";
import {
    completeMigrationJourney,
    MigrationAttempt,
    migrationContext,
    openMigrationJourney,
    reconcileMigrationTransactions
} from "@/services/usdv-migration/analytics";
import { readIntermediateUsdc, saveIntermediateUsdc } from "@/services/usdv-migration/storage";

import { getUsdvMigrationQueueSnapshot } from "./queue";
import { hasObservedMigrationSettlement, type MigrationSettlement } from "./settlement";
import { useMigrationSwapRecovery } from "./useMigrationSwapRecovery";
import { useUsdvMigrationQueue } from "./useUsdvMigrationQueue";

const ZERO = new BigDecimal(0);

/** Each click submits only the displayed action. Swaps never spend the wallet's USDC. */
export function useLiveUsdvMigration(enabled = true): { migrationCard: UsdvMigrationCardConfig | undefined } {
    const { publicKey } = useWallet();
    const owner = publicKey?.toString() ?? null;
    const ownerRef = useRef(owner);
    ownerRef.current = owner;
    const busy = useRef(false);
    const [recoveringReceipt, setRecoveringReceipt] = useState(false);
    const [analyticsRevision, setAnalyticsRevision] = useState(0);
    useEffect(() => {
        if (!owner) return;
        let active = true;
        const reconcile = (): void => {
            void reconcileMigrationTransactions(owner, enabled).then((changed) => {
                if (active && changed) setAnalyticsRevision((revision) => revision + 1);
            });
        };
        reconcile();
        window.addEventListener("focus", reconcile);
        return (): void => {
            active = false;
            window.removeEventListener("focus", reconcile);
        };
    }, [owner, enabled]);
    const recovery = useMigrationSwapRecovery(owner);
    const { balances, isLoading, error: balanceError } = useTokenBalancesData();
    const { refetchWithRetry } = useTokenBalancesActions();
    const queryClient = useQueryClient();
    const vault = useStakingVaultDerived();
    const queue = useUsdvMigrationQueue({ owner: publicKey });
    const [eligibility, setEligibility] = useState<{
        owner: string | null;
        result: "eligible" | "ineligible" | "error";
    } | null>(null);
    const canUnstake = useCanUnstakeSusdv(publicKey, balances.susdv ?? ZERO, (result) =>
        setEligibility({ owner, result })
    );
    const activeAttempt = useRef<MigrationAttempt | null>(null);
    const menuOpen = useRef(enabled);
    menuOpen.current = enabled;
    const observer = useMemo<TransactionObserver>(
        () => ({
            stage: (stage): void => activeAttempt.current?.observer.stage?.(stage),
            submitted: (signature): void => activeAttempt.current?.observer.submitted?.(signature),
            failed: (error, uncertain): void => activeAttempt.current?.observer.failed?.(error, uncertain)
        }),
        []
    );
    const unstake = useStartUnstakeTransaction(undefined, observer);
    const claim = useUnstakeTransaction(undefined, observer);
    const swap = useJupiterExecute();
    const [now, setNow] = useState(Date.now);
    const [settlement, setSettlement] = useState<MigrationSettlement | null>(null);
    const [phase, setPhase] = useState<"idle" | "pending" | "success" | "error">("idle");
    useEffect(() => {
        if (phase !== "error" || !owner) return;
        void reconcileMigrationTransactions(owner, enabled).then((changed) => {
            if (changed && ownerRef.current === owner) setAnalyticsRevision((revision) => revision + 1);
        });
    }, [phase, owner, enabled]);
    const [submittedQuote, setSubmittedQuote] = useState<JupiterUltraQuoteResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    useEffect(() => {
        setSettlement(null);
        setPhase("idle");
        setError(null);
        setSubmittedQuote(null);
    }, [owner]);
    const queueReady = queue.owner === owner && queue.status === "loaded" && vault.isQueueLoaded;
    const livePlan = useMemo<UsdvMigrationPlan>(() => {
        const entries = queueReady ? (getUsdvMigrationQueueSnapshot(vault.unstakingQueue)?.cooldownEntries ?? []) : [];
        const cooling = entries.filter((entry) => entry.endsAt.getTime() > now);
        return {
            legacyUsdv: balances.legacyUsdv ?? ZERO,
            stakedUsdv: canUnstake ? (balances.susdv ?? ZERO) : ZERO,
            expectedStakedUsdv: vault.exchangeRate ? (balances.susdv ?? ZERO).multiply(vault.exchangeRate) : null,
            claimableUsdv: entries
                .filter((entry) => entry.endsAt.getTime() <= now)
                .reduce((sum, entry) => sum.add(entry.usdv ?? ZERO), ZERO),
            coolingUsdv: cooling.reduce((sum, entry) => sum.add(entry.usdv ?? ZERO), ZERO),
            coolingEndsAt: cooling.reduce<Date | null>(
                (date, entry) => (!date || entry.endsAt < date ? entry.endsAt : date),
                null
            ),
            cooldownDays: vault.cooldownDays
        };
    }, [balances, canUnstake, now, queueReady, vault.unstakingQueue, vault.exchangeRate, vault.cooldownDays]);
    useEffect(() => {
        if (!livePlan.coolingEndsAt) return;
        const timer = setTimeout(
            () => setNow(Date.now()),
            Math.min(Math.max(1, livePlan.coolingEndsAt.getTime() - Date.now()), 2_147_483_647)
        );
        return (): void => clearTimeout(timer);
    }, [livePlan.coolingEndsAt]);
    useEffect(() => {
        if (settlement?.owner === owner && queueReady && hasObservedMigrationSettlement(settlement, livePlan))
            setSettlement(null);
    }, [livePlan, owner, queueReady, settlement]);
    const plan = {
        ...(settlement?.owner === owner ? settlement.plan : livePlan),
        usdcUsdv: new BigDecimal(recovery.record.amount)
    };
    const hostname = typeof location === "undefined" ? "" : location.hostname;
    const localTest = ["localhost", "127.0.0.1", "[::1]"].includes(hostname);
    const step = getUsdvMigrationCurrentStep(plan);
    // A finished walk stays on "success" until the flow's Migration Complete
    // screen hands back (stepFlow.onComplete). Resetting it here would unmount
    // the flow mid-screen.
    const walkFinished = step === null;
    useEffect(() => {
        if (!localTest || phase !== "success" || walkFinished) return;
        const timer = setTimeout(() => setPhase("idle"), 3000);
        return (): void => clearTimeout(timer);
    }, [localTest, phase, walkFinished]);
    const swapAmount =
        step?.index === 4
            ? plan.usdcUsdv
            : localTest && plan.legacyUsdv.gt("0.5")
              ? new BigDecimal("0.5")
              : plan.legacyUsdv;
    const displayPlan = { ...plan, legacyUsdv: step?.index === 3 ? swapAmount : plan.legacyUsdv };
    const isSwap = step?.index === 3 || step?.index === 4;
    const inputMint = step?.index === 4 ? WELL_KNOWN_TOKENS_STRINGS.USDC : LEGACY_USDV_MINT;
    const outputMint = step?.index === 4 ? USDV_MINT : WELL_KNOWN_TOKENS_STRINGS.USDC;
    const inputDecimals = step?.index === 4 ? 6 : LEGACY_USDV_DECIMALS;
    const params =
        enabled && isSwap && owner
            ? {
                  inputMint,
                  outputMint,
                  amount: swapAmount.multiply(BigDecimal.pow10(inputDecimals)).toBigint().toString(),
                  taker: owner
              }
            : null;
    const quote = useJupiterQuote(params);
    const matchedQuote = buildJupiterExecuteParams(quote.data, params, owner);
    const executeParams = matchedQuote?.swapMode === "ExactIn" ? matchedQuote : null;
    const disabled =
        !enabled ||
        isLoading ||
        !!balanceError ||
        !queueReady ||
        recovery.pending ||
        phase === "pending" ||
        !!step?.waiting ||
        (isSwap && (!executeParams || quote.isFetching || quote.isError));
    const transactionError =
        step?.index === 1
            ? unstake.state.errorMessage
            : step?.index === 2
              ? claim.state.errorMessage
              : swap.state.errorMessage;
    const run = async (): Promise<void> => {
        if (!owner || !step) return;
        const attempt = new MigrationAttempt(owner, migrationStep(step.index)!, undefined, () => menuOpen.current);
        if (!publicKey || disabled || busy.current) {
            attempt.block(
                !publicKey
                    ? "wallet_unavailable"
                    : recovery.pending
                      ? "recovery_required"
                      : step.waiting
                        ? "cooldown"
                        : busy.current || phase === "pending"
                          ? "already_pending"
                          : "quote_unavailable"
            );
            return;
        }
        if (phase === "error")
            captureWalletEvent(
                "migration:retry_clicked",
                { ...attempt.properties(), retry_target: "transaction" },
                attempt.owner
            );
        busy.current = true;
        setError(null);
        const requestOwner = owner;
        try {
            const { acquired } = await withWalletBalanceMutationLock("usdv-migration", async () => {
                if (ownerRef.current !== requestOwner) {
                    attempt.block("wallet_unavailable");
                    return;
                }
                activeAttempt.current = attempt;
                attempt.start();
                setPhase("pending");
                let next: UsdvMigrationPlan;
                if (step.index === 1) {
                    const signature = await unstake.startUnstake(plan.stakedUsdv);
                    if (!signature) {
                        attempt.finish("failed", {
                            error_code: "transaction_not_completed",
                            error_category: "unknown"
                        });
                        if (ownerRef.current === requestOwner) setPhase("error");
                        return;
                    }
                    next = {
                        ...plan,
                        stakedUsdv: ZERO,
                        expectedStakedUsdv: null,
                        coolingUsdv: (plan.coolingUsdv ?? ZERO).add(plan.expectedStakedUsdv ?? plan.stakedUsdv),
                        coolingEndsAt: plan.coolingEndsAt ?? new Date(Date.now() + plan.cooldownDays * 86400000)
                    };
                } else if (step.index === 2) {
                    const result = await claim.claimAll();
                    if (!result) {
                        attempt.finish("failed", {
                            error_code: "transaction_not_completed",
                            error_category: "unknown"
                        });
                        if (ownerRef.current === requestOwner) setPhase("error");
                        return;
                    }
                    next = { ...plan, legacyUsdv: plan.legacyUsdv.add(result.assetsClaimed), claimableUsdv: ZERO };
                } else {
                    if (!executeParams) throw new Error("Wait for a valid swap quote.");
                    setSubmittedQuote(quote.data ?? null);
                    const remainingLegacy = step.index === 3 ? plan.legacyUsdv.subtract(swapAmount) : plan.legacyUsdv;
                    const receipt = await swap.executeWithReceipt(executeParams, {
                        beforeSubmit: (bytes): Promise<void> =>
                            recovery.prepare(
                                bytes,
                                step.index === 3 ? "legacy-to-usdc" : "usdc-to-usdv",
                                executeParams.expectedInputAmount,
                                remainingLegacy.toString(),
                                attempt.record
                            ),
                        observer: attempt.observer,
                        diagnostics: { flow: "usdv-migration" }
                    });
                    if (!receipt?.signature) {
                        const pending = readIntermediateUsdc(requestOwner);
                        attempt.finish(pending.pendingSwap || pending.unresolvedSignature ? "deferred" : "failed", {
                            error_code: "transaction_not_completed",
                            error_category: "unknown"
                        });
                        if (ownerRef.current === requestOwner) setPhase("error");
                        return;
                    }
                    try {
                        recovery.complete(receipt);
                    } catch (accountingError) {
                        attempt.finish("deferred", { ...eventError(accountingError), reason: "storage_failed" });
                        throw accountingError;
                    }
                    const pending = readIntermediateUsdc(requestOwner);
                    if (pending.pendingSwap || pending.unresolvedSignature) {
                        attempt.finish("deferred");
                    }
                    next = { ...plan, legacyUsdv: remainingLegacy };
                }
                const pending = readIntermediateUsdc(requestOwner);
                if (!pending.pendingSwap && !pending.unresolvedSignature) attempt.finish("succeeded");
                if (step.index === 1)
                    captureWalletEvent(
                        "migration:cooldown_started",
                        { ...attempt.properties(), cooldown_duration_ms: plan.cooldownDays * 86400000 },
                        attempt.owner
                    );
                if (ownerRef.current !== requestOwner) return;
                // Keep settled inputs consumed until RPC catches up. A second click cannot spend them again.
                setSettlement({
                    owner: requestOwner,
                    before: plan,
                    plan: next,
                    action: step.index === 1 ? "unstake" : step.index === 2 ? "claim" : "swap"
                });
                setPhase("success");
                await Promise.allSettled([
                    refetchWithRetry(),
                    queue.refreshAfterClaim(publicKey),
                    queryClient.invalidateQueries({ queryKey: ["transactionHistory"] })
                ]);
            });
            if (!acquired) {
                attempt.block("already_pending");
                setError("Another wallet transaction is in progress. Wait for it to finish.");
            }
        } catch (caught) {
            attempt.finish("failed", eventError(caught));
            if (ownerRef.current === requestOwner) {
                setPhase("error");
                setError(TransactionError.fromCaught(caught).userMessage);
            }
        } finally {
            busy.current = false;
        }
    };
    const retryReceipt = async (): Promise<void> => {
        if (!owner || !publicKey || !recovery.pending || busy.current) return;
        const requestOwner = owner;
        const stored = readIntermediateUsdc(owner);
        const record = stored.pendingSwap?.analytics ?? stored.analytics;
        const restored = record ?? {
            journeyId: openMigrationJourney(owner).migration_journey_id!,
            attemptId: eventId(),
            step: migrationStep(stored.pendingSwap?.leg === "usdc-to-usdv" ? 4 : 3)!,
            number: 1,
            origin: "imported" as const
        };
        if (!record) {
            // Optional tracking metadata must not stop receipt recovery.
            try {
                saveIntermediateUsdc(owner, { ...stored, analytics: restored });
            } catch {
                /* Keep the current check usable. */
            }
        }
        const attempt = new MigrationAttempt(owner, restored.step, restored, () => menuOpen.current);
        const checkId = eventId();
        captureWalletEvent(
            "migration:retry_clicked",
            { ...attempt.properties(), retry_target: "receipt" },
            attempt.owner
        );
        captureWalletEvent(
            "migration:check_started",
            { ...attempt.properties(), check_id: checkId, check_type: "receipt", trigger: "retry" },
            attempt.owner
        );
        busy.current = true;
        setRecoveringReceipt(true);
        try {
            const result = await recovery.recover();
            captureWalletEvent(
                "migration:check_completed",
                {
                    ...attempt.properties(),
                    check_id: checkId,
                    check_type: "receipt",
                    result: result.stillPending ? "pending" : result.failed ? "failed" : "confirmed"
                },
                attempt.owner
            );
            if (result.stillPending) {
                if (ownerRef.current === requestOwner)
                    setError("The previous swap is still being confirmed. Check its status again.");
                return;
            }
            attempt.finish(result.failed ? "failed" : "succeeded", {}, "recovery");
            if (ownerRef.current === requestOwner) {
                if (!result.failed && result.pending?.leg === "legacy-to-usdc") {
                    const remaining = new BigDecimal(result.pending.remainingLegacyUsdv);
                    const next = { ...plan, legacyUsdv: plan.legacyUsdv.lt(remaining) ? plan.legacyUsdv : remaining };
                    setSettlement({ owner: requestOwner, action: "swap", before: plan, plan: next });
                }
                setPhase(result.failed ? "error" : "idle");
                setError(result.failed ? "The previous swap did not settle. You can try again." : null);
                await Promise.allSettled([refetchWithRetry(), queue.refreshAfterClaim(publicKey)]);
            }
        } catch (caught) {
            captureWalletEvent(
                "migration:check_failed",
                { ...attempt.properties(), check_id: checkId, check_type: "receipt", ...eventError(caught) },
                attempt.owner
            );
            if (ownerRef.current === requestOwner) setError(TransactionError.fromCaught(caught).userMessage);
        } finally {
            busy.current = false;
            setRecoveringReceipt(false);
        }
    };
    const claimObserved = useRef<{ owner: string | null; key: string }>({ owner: null, key: "" });
    useEffect(() => {
        if (!owner || !queueReady) return;
        // Local identity only. Never send withdrawal timestamps or amounts.
        const claimKey = (vault.unstakingQueue ?? [])
            .filter((entry) => entry.timestamp * 1000 <= now)
            .map((entry) => `${entry.timestamp}:${entry.amount.toString()}`)
            .sort()
            .join("|");
        if (claimKey && (claimObserved.current.owner !== owner || claimObserved.current.key !== claimKey)) {
            captureWalletEvent(
                "migration:claim_detected",
                {
                    ...migrationContext(owner),
                    observation: claimObserved.current.owner === owner ? "became_claimable" : "initial",
                    menu_open: enabled
                },
                ownerForAddress(owner)
            );
        }
        claimObserved.current = { owner, key: claimKey };
        if (
            !isLoading &&
            !balanceError &&
            !settlement &&
            !recovery.pending &&
            !getUsdvMigrationCurrentStep({ ...livePlan, usdcUsdv: plan.usdcUsdv }) &&
            (!(balances.susdv?.gt(0) && !canUnstake) ||
                (eligibility?.owner === owner && eligibility.result === "ineligible"))
        ) {
            completeMigrationJourney(owner, {
                completion_source: phase === "success" ? "live" : "recovery",
                menu_open: enabled,
                has_legacy_dust: !!balances.susdv?.gt(0) && !canUnstake
            });
        }
    }, [
        owner,
        queueReady,
        livePlan,
        enabled,
        isLoading,
        balanceError,
        settlement,
        recovery.pending,
        plan.usdcUsdv,
        phase,
        balances.susdv,
        canUnstake,
        analyticsRevision,
        eligibility,
        vault.unstakingQueue,
        now
    ]);

    const displayQuote =
        isSwap && phase !== "pending" && !(localTest && phase === "success")
            ? quote.data
            : (submittedQuote ?? quote.data);
    const quoteMatches = !!executeParams;
    const formatRaw = (raw: string): string =>
        new BigDecimal(raw)
            .divide(BigDecimal.pow10(displayQuote?.outputMint === USDV_MINT ? USDV_DECIMALS : 6))
            .toString();
    const hasSurface = !!step || phase === "success" || recovery.pending || (owner && !queueReady);
    return {
        migrationCard: hasSurface
            ? {
                  plan: displayPlan,
                  analytics: {
                      properties: {
                          ...migrationContext(owner),
                          has_legacy_usdv: plan.legacyUsdv.gt(0),
                          has_legacy_susdv: plan.stakedUsdv.gt(0),
                          has_claimable: plan.claimableUsdv?.gt(0),
                          has_cooling: plan.coolingUsdv?.gt(0),
                          has_intermediate_usdc: plan.usdcUsdv.gt(0),
                          remaining_step_count: step?.total ?? 0
                      },
                      onOpen: () => (owner ? openMigrationJourney(owner) : {}),
                      state: recovery.pending
                          ? "recovery_required"
                          : !queueReady
                            ? queue.status === "error"
                                ? "check_failed"
                                : "loading"
                            : !step && phase === "success"
                              ? "completed"
                              : step?.waiting
                                ? "cooldown"
                                : "available",
                      stepState: recovery.pending
                          ? "recovery_required"
                          : phase === "pending"
                            ? "pending"
                            : phase === "success"
                              ? "succeeded"
                              : phase === "error"
                                ? "failed"
                                : step?.waiting
                                  ? "cooldown"
                                  : isSwap && quote.isFetching
                                    ? "quote_loading"
                                    : isSwap && quote.isError
                                      ? "quote_failed"
                                      : isSwap && !quoteMatches
                                        ? "quote_mismatch"
                                        : "ready"
                  },
                  state:
                      phase === "pending"
                          ? { phase: "running", action: step?.index === 2 ? "claim" : "start", legacy: "pending" }
                          : phase === "success" && !step
                            ? { phase: "success", kind: "upgrade", cooldownEndsAt: null, claimableAfterStart: false }
                            : { phase: "available" },
                  onAction: run,
                  onDismiss: (): void => {
                      if (owner)
                          captureWalletEvent("migration:done_clicked", migrationContext(owner), ownerForAddress(owner));
                      setPhase("idle");
                  },
                  queueLoadRecovery: recovery.pending
                      ? {
                            status: recoveringReceipt ? "retrying" : "error",
                            onRetry: (): void => {
                                void retryReceipt();
                            }
                        }
                      : !queueReady
                        ? queue.status === "error"
                            ? { status: "error", onRetry: queue.retry }
                            : { status: "loading" }
                        : undefined,
                  stepFlow: {
                      phase,
                      onAction: run,
                      disabled,
                      error:
                          error ||
                          (recovery.pending ? "Check the previous swap's status before continuing." : null) ||
                          (phase === "error"
                              ? transactionError || "The transaction did not complete. Try again."
                              : null) ||
                          (isSwap && quote.isError ? "No swap quote is available. Wait for a new quote." : null),
                      receiveAmount:
                          displayQuote &&
                          (quoteMatches || phase === "success" || (phase === "pending" && !!submittedQuote))
                              ? formatRaw(displayQuote.outAmount)
                              : undefined,
                      minimumReceiveAmount:
                          displayQuote?.otherAmountThreshold &&
                          (quoteMatches || phase === "success" || (phase === "pending" && !!submittedQuote))
                              ? formatRaw(displayQuote.otherAmountThreshold)
                              : undefined,
                      usdvBalance: balances.usdv ?? null,
                      onComplete: (): void => setPhase("idle")
                  }
              }
            : undefined
    };
}
